<template>
  <div>
    <!-- Toolbar: Filters, Record Count, Pagination -->
    <div
      data-testid="transaction-matching-consolidated-results__toolbar"
      class="consolidated-results__toolbar"
    >
      <!-- Left Section: Filter Controls -->
      <div class="consolidated-results__toolbar-left">
        <!-- Filter Button -->
        <button
          data-testid="transaction-matching-consolidated-results__filter-btn"
          class="consolidated-results__action-btn"
          @click="$emit('open-filter')"
        >
          <img src="/img/icons/fbc-icons/filter.svg" alt="Filter" class="consolidated-results__action-icon" />
          <span>Filters</span>
          <span
            v-if="activeFilterCount > 0"
            data-testid="transaction-matching-consolidated-results__filter-badge"
            class="consolidated-results__badge"
          >
            {{ activeFilterCount }}
          </span>
        </button>

        <!-- Clear Filters Button - Hidden: Users should use filter popup to clear filters -->
        <button
          v-if="false && hasActiveFilters"
          data-testid="transaction-matching-consolidated-results__clear-filters-btn"
          class="consolidated-results__icon-btn"
          title="Clear filters"
          @click="$emit('clear-filters')"
        >
          <img src="/img/icons/fbc-icons/clear-filter.svg" alt="Clear Filters" class="consolidated-results__icon-btn-img" />
        </button>

        <!-- Sort Button -->
        <button
          data-testid="transaction-matching-consolidated-results__sort-btn"
          class="consolidated-results__action-btn"
          @click="$emit('open-sort')"
        >
          <img src="/img/icons/fbc-icons/sort.svg" alt="Sort" class="consolidated-results__action-icon" />
          <span>Sort</span>
          <span
            v-if="activeSortCount > 0"
            data-testid="transaction-matching-consolidated-results__sort-badge"
            class="consolidated-results__badge"
          >
            {{ activeSortCount }}
          </span>
        </button>

        <!-- Clear Sort Button - Hidden: Users should use sort popup to clear sort -->
        <button
          v-if="false && appliedSortLength > 0"
          data-testid="transaction-matching-consolidated-results__clear-sort-btn"
          class="consolidated-results__icon-btn"
          title="Clear sort"
          @click="$emit('clear-sort')"
        >
          <img src="/img/icons/fbc-icons/clear-sort.svg" alt="Clear Sort" class="consolidated-results__icon-btn-img" />
        </button>

        <!-- Save Column Order Button -->
        <button
          data-testid="transaction-matching-consolidated-results__save-order-btn"
          class="consolidated-results__action-btn"
          :class="{ 'consolidated-results__action-btn--highlighted': hasUnsavedHeaderChanges }"
          :disabled="!hasUnsavedHeaderChanges"
          title="Save Column Order"
          @click="$emit('save-header-order')"
        >
          <v-icon color="white" small>mdi-bookmark</v-icon>
          <span>Save Order</span>
        </button>

        <!-- Record Count -->
        <span
          data-testid="transaction-matching-consolidated-results__record-count"
          class="consolidated-results__record-count"
        >
          <strong class="consolidated-results__record-count-value">{{ formatNumber(totalCount) }}</strong>&nbsp;records
        </span>
      </div>

      <!-- Right Section: Download, Bulk Matching & Pagination -->
      <div class="consolidated-results__toolbar-right">
        <!-- V1: Original icon-only buttons -->
        <template v-if="variant === 'v1'">
          <button
            data-testid="transaction-matching-consolidated-results__download-template-btn"
            class="consolidated-results__icon-btn-primary"
            title="Download Template"
            @click="$emit('download-template')"
          >
            <img src="/img/icons/fbc-icons/download-template.svg" alt="Download Template" class="consolidated-results__icon-btn-img-white" />
          </button>

          <button
            data-testid="transaction-matching-consolidated-results__bulk-manual-matching-btn"
            class="consolidated-results__icon-btn-primary"
            title="Bulk Manual Matching"
            @click="$emit('bulk-manual-match')"
          >
            <img src="/img/icons/tm-icons/bulk-manual-matching.svg" alt="Bulk Manual Matching" class="consolidated-results__icon-btn-img-white" />
          </button>
        </template>

        <!-- V2: Generate Excel split-button + white Bulk Manual Tag -->
        <template v-if="variant === 'v2'">
          <DownloadExcel
            class="consolidated-results__download-excel-v2"
            button-text="Generate Excel"
            :download-history="downloadHistory"
            :loading-history="loadingHistory"
            :current-user-id="$store.state.user ? String($store.state.user.id || $store.state.user._id || '') : ''"
            :total-count="totalCount"
            :max-export-records="maxExportRecords"
            :compact="true"
            @generate="$emit('generate-excel')"
            @refresh="$emit('refresh-download-history')"
            @download="$emit('download-history-file', $event)"
          />

          <v-tooltip v-if="showManualTag" bottom>
            <template v-slot:activator="{ on, attrs }">
              <button
                data-testid="transaction-matching-consolidated-results__bulk-manual-tag-btn"
                class="consolidated-results__icon-btn"
                v-bind="attrs"
                v-on="on"
                @click="$emit('bulk-manual-match')"
              >
                <img src="/img/icons/tm-icons/bulk-manual-matching-dark.svg" alt="Bulk Manual Tag" class="consolidated-results__icon-btn-img" />
              </button>
            </template>
            <span>Bulk Manual Tag</span>
          </v-tooltip>
        </template>

        <!-- Divider between action buttons and pagination (V2 only) -->
        <div v-if="variant === 'v2' && pageCount > 1" class="consolidated-results__toolbar-divider"></div>

        <!-- Pagination -->
        <div
          v-if="pageCount > 1"
          data-testid="transaction-matching-consolidated-results__pagination"
          :class="variant === 'v2' ? 'consolidated-results__pagination-v2' : 'consolidated-results__pagination'"
        >
          <button
            data-testid="transaction-matching-consolidated-results__prev-button"
            :disabled="page === 1"
            :class="variant === 'v2' ? 'consolidated-results__pagination-v2-btn' : 'consolidated-results__pagination-btn'"
            @click="$emit('page-change', page - 1)"
          >
            <v-icon :size="variant === 'v2' ? 15 : undefined">mdi-chevron-left</v-icon>
          </button>
          <span
            data-testid="transaction-matching-consolidated-results__page-info"
            :class="variant === 'v2' ? 'consolidated-results__page-info-v2' : 'consolidated-results__page-info'"
          >
            Page <span :class="{ 'font-weight-bold': variant === 'v2' }">{{ page }}</span> of <span :class="{ 'font-weight-bold': variant === 'v2' }">{{ formatNumber(pageCount) }}</span>
          </span>
          <button
            data-testid="transaction-matching-consolidated-results__next-button"
            :disabled="page === pageCount"
            :class="variant === 'v2' ? 'consolidated-results__pagination-v2-btn' : 'consolidated-results__pagination-btn'"
            @click="$emit('page-change', page + 1)"
          >
            <v-icon :size="variant === 'v2' ? 15 : undefined">mdi-chevron-right</v-icon>
          </button>

          <!-- Page Jump -->
          <div
            data-testid="transaction-matching-consolidated-results__page-jump"
            :class="variant === 'v2' ? 'consolidated-results__page-jump-v2' : 'consolidated-results__page-jump'"
          >
            <input
              data-testid="transaction-matching-consolidated-results__page-jump-input"
              :type="variant === 'v2' ? 'text' : 'number'"
              v-model.number="pageJumpInput"
              :min="1"
              :max="pageCount"
              :class="variant === 'v2' ? 'consolidated-results__page-jump-v2-input' : 'consolidated-results__page-jump-input'"
              @keyup.enter="jumpToPage"
            />
            <button
              data-testid="transaction-matching-consolidated-results__page-jump-btn"
              :class="variant === 'v2' ? 'consolidated-results__page-jump-v2-btn' : 'consolidated-results__page-jump-btn'"
              :disabled="!isValidPageJump"
              @click="jumpToPage"
            >
              Go
            </button>
          </div>
        </div>
      </div>
    </div>

    <TMResultsSortDropdown
      v-if="showSortModal"
      test-id-prefix="transaction-matching-consolidated-results"
      sort-btn-test-id="transaction-matching-consolidated-results__sort-btn"
      :active-sort-tab="activeSortTab"
      :available-sort-columns="availableSortColumns"
      :saved-sort="savedSort"
      :playground-sort="playgroundSort"
      :enable-saved-filter-sort="enableSavedFilterSort"
      @close="$emit('close-sort')"
      @update:active-sort-tab="$emit('update:active-sort-tab', $event)"
      @toggle-sort-column="$emit('toggle-sort-column', $event)"
      @set-sort-direction="(key, direction) => $emit('set-sort-direction', key, direction)"
      @reset-sort="$emit('reset-sort')"
      @apply-sort="$emit('apply-sort')"
    />
  </div>
</template>

<script>
import DownloadExcel from '../TransationMatching/downloadExcel.vue';
import TMResultsSortDropdown from './TMResultsSortDropdown.vue';
import { formatNumber } from './tmResultsDataTableUtils.js';

export default {
  name: 'TMResultsDataTableToolbar',

  components: {
    DownloadExcel,
    TMResultsSortDropdown,
  },

  props: {
    variant: {
      type: String,
      default: 'v1',
      validator: (value) => ['v1', 'v2'].includes(value)
    },
    activeFilterCount: {
      type: Number,
      default: 0
    },
    hasActiveFilters: {
      type: Boolean,
      default: false
    },
    activeSortCount: {
      type: Number,
      default: 0
    },
    appliedSortLength: {
      type: Number,
      default: 0
    },
    hasUnsavedHeaderChanges: {
      type: Boolean,
      default: false
    },
    totalCount: {
      type: Number,
      default: 0
    },
    page: {
      type: Number,
      default: 1
    },
    pageCount: {
      type: Number,
      default: 0
    },
    downloadHistory: {
      type: Array,
      default: () => []
    },
    loadingHistory: {
      type: Boolean,
      default: false
    },
    maxExportRecords: {
      type: Number,
      required: true
    },
    showSortModal: {
      type: Boolean,
      default: false
    },
    activeSortTab: {
      type: String,
      default: 'saved'
    },
    availableSortColumns: {
      type: Array,
      default: () => []
    },
    savedSort: {
      type: Array,
      default: () => []
    },
    playgroundSort: {
      type: Array,
      default: () => []
    },
    enableSavedFilterSort: {
      type: Boolean,
      default: true
    },
    showManualTag: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      pageJumpInput: null
    };
  },

  computed: {
    isValidPageJump() {
      return (
        this.pageJumpInput !== null &&
        this.pageJumpInput >= 1 &&
        this.pageJumpInput <= this.pageCount &&
        this.pageJumpInput !== this.page
      );
    },
  },

  methods: {
    formatNumber(num) {
      return formatNumber(num);
    },

    jumpToPage() {
      if (this.isValidPageJump) {
        this.$emit('page-change', this.pageJumpInput);
        this.pageJumpInput = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './TMResultsDataTable.scss';
</style>
